// Page t("auditPage.title") (Lot 6C) — liste paginée des événements sensibles
// du tenant courant (session, équipe, invitations).
//
// Réservée à l'ADMIN — pattern identique à MembersPage : on affiche un
// message de refus si l'utilisateur n'a pas le rôle plutôt que de laisser
// passer un appel API qui sera refusé en 403.
//
// L'objectif ici est la consultation ponctuelle (audit de sécurité, suivi
// d'une action douteuse) — pas un tableau temps-réel. On expose donc une
// pagination offset simple + un filtre par type d'action.

import { useEffect, useMemo, useState } from "react";

import {
  auditApi,
  type AuditAction,
  type AuditEventPage,
  type AuditEventPublic,
} from "../api/audit";
import { ApiError } from "../api/client";
import { useTranslation } from "react-i18next";
import { useTranslation } from "react-i18next";
import { AppShell } from "../components/AppShell";
import { useAuthStore } from "../stores/auth";

const PAGE_SIZE = 50;

function getActionLabels(t: (k: string) => string): Record<string, string> {
  return {
    session_started: t("auditPage.actionLogin"),
    member_invited: t("auditPage.actionInvited"),
    member_added_direct: t("auditPage.actionAdded"),
    invitation_revoked: t("auditPage.actionRevoked"),
    invitation_accepted: t("auditPage.actionAccepted"),
    member_role_changed: t("auditPage.actionRoleChanged"),
    member_removed: t("auditPage.actionRemoved"),
  };
}

function getActionOptions(t: (k: string) => string) {
  const labels = getActionLabels(t);
  return [
    { value: "" as AuditAction | "", label: t("auditPage.allActions") },
    { value: "session_started" as AuditAction, label: labels.session_started },
    { value: "member_invited" as AuditAction, label: labels.member_invited },
    { value: "member_added_direct" as AuditAction, label: labels.member_added_direct },
    { value: "invitation_revoked" as AuditAction, label: labels.invitation_revoked },
    { value: "invitation_accepted" as AuditAction, label: labels.invitation_accepted },
    { value: "member_role_changed" as AuditAction, label: labels.member_role_changed },
    { value: "member_removed" as AuditAction, label: labels.member_removed },
  ];
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function actionLabel(action: AuditAction, labels: Record<string, string>): string {
  return labels[action] ?? action;
}

/**
 * Composition FR d'un résumé de cible lisible : on tire le meilleur signal
 * disponible dans le payload (email/rôle dans les métadonnées, puis
 * target_type + id tronqué en fallback). Le JSON brut reste accessible à
 * l'expand — ici on veut juste une ligne scannable rapidement.
 */
function formatTarget(event: AuditEventPublic): string {
  const meta = event.metadata ?? {};
  const email = typeof meta.email === "string" ? meta.email : null;
  const role = typeof meta.role === "string" ? meta.role : null;
  const oldRole = typeof meta.old_role === "string" ? meta.old_role : null;
  const newRole = typeof meta.new_role === "string" ? meta.new_role : null;
  const via = typeof meta.via === "string" ? meta.via : null;

  switch (event.action) {
    case "session_started":
      return via ? `via ${via}` : "—";
    case "member_invited":
    case "invitation_revoked":
    case "invitation_accepted":
      return role && email ? `${email} (${role})` : email ?? "—";
    case "member_added_direct":
      return role && email ? `${email} → ${role}` : email ?? "—";
    case "member_role_changed":
      return oldRole && newRole ? `${oldRole} → ${newRole}` : "—";
    case "member_removed":
      return role ? `rôle: ${role}` : "—";
    default:
      return event.target_id ? event.target_id.slice(0, 8) : "—";
  }
}

function formatActor(event: AuditEventPublic): string {
  if (!event.actor) return "Système";
  return event.actor.full_name || event.actor.email;
}

export function AuditPage() {
  const { t } = useTranslation();
  const ACTION_LABELS = getActionLabels(t);
  const ACTION_OPTIONS = getActionOptions(t);
  const currentRole = useAuthStore((s) => s.currentRole);
  const isAdmin = currentRole === "admin";

  const [page, setPage] = useState<AuditEventPage | null>(null);
  const [offset, setOffset] = useState(0);
  const [action, setAction] = useState<AuditAction | "">("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await auditApi.list({
          limit: PAGE_SIZE,
          offset,
          action: action === "" ? undefined : action,
        });
        if (!cancelled) setPage(data);
      } catch (err) {
        if (cancelled) return;
        const msg =
          err instanceof ApiError
            ? err.detail
            : err instanceof Error
              ? err.message
              : "Erreur de chargement";
        setError(msg);
        setPage({ items: [], total: 0, limit: PAGE_SIZE, offset });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [isAdmin, offset, action]);

  const hasNext = useMemo(() => {
    if (!page) return false;
    return offset + page.items.length < page.total;
  }, [page, offset]);
  const hasPrev = offset > 0;

  function onChangeFilter(value: AuditAction | "") {
    setAction(value);
    setOffset(0); // On repart à la première page quand on change de filtre.
  }

  if (!isAdmin) {
    return (
      <AppShell>
        <header className="mb-6">
          <h1 className="text-2xl font-semibold text-slate-900">
            {t("auditPage.title")}
          </h1>
        </header>
        <div className="bg-amber-50 border border-amber-200 text-amber-800 p-4 rounded-md">
          Seuls les administrateurs peuvent consulter le journal d'audit.
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">
          {t("auditPage.title")}
        </h1>
        <p className="text-sm text-slate-500">
          {t("auditPage.subtitle")}
        </p>
      </header>

      <div className="mb-4 flex items-center gap-3">
        <label htmlFor="audit_action" className="text-sm text-slate-600">
          {t("auditPage.colAction")} :
        </label>
        <select
          id="audit_action"
          aria-label="Filtrer par action"
          value={action}
          onChange={(e) => onChangeFilter(e.target.value as AuditAction | "")}
          className="rounded-md border border-slate-300 text-sm px-2 py-1"
        >
          {ACTION_OPTIONS.map((opt) => (
            <option key={opt.value || "all"} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div
          className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3"
          role="alert"
        >
          {error}
        </div>
      )}

      {page === null ? (
        <p className="text-sm text-slate-500">{t("common.loading")}</p>
      ) : page.items.length === 0 ? (
        <p className="text-sm text-slate-500">
          {t("auditPage.noEvents")}
        </p>
      ) : (
        <section className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-2">{t("auditPage.colDate")}</th>
                <th className="text-left px-4 py-2">{t("auditPage.colAction")}</th>
                <th className="text-left px-4 py-2">{t("auditPage.colUser")}</th>
                <th className="text-left px-4 py-2">{t("auditPage.colResource")}</th>
                <th className="text-left px-4 py-2">IP</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {page.items.map((e) => (
                <tr key={e.id}>
                  <td className="px-4 py-2 text-slate-700 whitespace-nowrap">
                    {formatDateTime(e.created_at)}
                  </td>
                  <td className="px-4 py-2 text-slate-800">
                    {actionLabel(e.action, ACTION_LABELS)}
                  </td>
                  <td className="px-4 py-2 text-slate-600">
                    {formatActor(e)}
                  </td>
                  <td className="px-4 py-2 text-slate-600">
                    {formatTarget(e)}
                  </td>
                  <td className="px-4 py-2 text-slate-500 font-mono text-xs">
                    {e.ip_address ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {page !== null && page.total > 0 && (
        <footer className="mt-4 flex items-center justify-between text-sm text-slate-500">
          <span>
            {offset + 1}–{offset + page.items.length} sur {page.total}
            {loading ? " — chargement…" : ""}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              className="px-3 py-1 rounded-md border border-slate-300 text-slate-600 disabled:text-slate-300 disabled:border-slate-200"
              disabled={!hasPrev || loading}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              Précédent
            </button>
            <button
              type="button"
              className="px-3 py-1 rounded-md border border-slate-300 text-slate-600 disabled:text-slate-300 disabled:border-slate-200"
              disabled={!hasNext || loading}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Suivant
            </button>
          </div>
        </footer>
      )}
    </AppShell>
  );
}
